import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase, db, realtime } from '../lib/supabase';
import '../styles/Dashboard.css';

const CATEGORIES = [
  { value: 'support', label: 'Support', desc: 'Technical help, account issues, proxy problems' },
  { value: 'commercial', label: 'Commercial', desc: 'Business inquiries, partnerships, bulk orders' },
  { value: 'resell', label: 'Resell', desc: 'Reseller program, custom pricing, API access' },
  { value: 'other', label: 'Other', desc: 'Anything else' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const MAX_OPEN_TICKETS = 3;

const TicketsPage = () => {
  const { user } = useAuth();
  const [view, setView] = useState('list'); // list | create | detail
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [openCount, setOpenCount] = useState(0);

  // Create form state
  const [category, setCategory] = useState('support');
  const [priority, setPriority] = useState('medium');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Detail view message input
  const [replyMessage, setReplyMessage] = useState('');
  const messagesEndRef = useRef(null);
  const messageChannelRef = useRef(null);
  const ticketChannelRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTickets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error: fetchError } = await db.tickets.getAll(user.id);
    if (!fetchError && data) {
      setTickets(data);
      setOpenCount(data.filter(t => t.status === 'open').length);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Subscribe to ticket changes
  useEffect(() => {
    if (!user) return;
    const channel = realtime.subscribeToTickets(user.id, (payload) => {
      if (payload.eventType === 'UPDATE') {
        setTickets(prev => prev.map(t => t.id === payload.new.id ? payload.new : t));
        if (selectedTicket && selectedTicket.id === payload.new.id) {
          setSelectedTicket(payload.new);
        }
      } else if (payload.eventType === 'INSERT') {
        setTickets(prev => [payload.new, ...prev]);
      }
    });
    ticketChannelRef.current = channel;
    return () => {
      if (ticketChannelRef.current) {
        realtime.unsubscribe(ticketChannelRef.current);
      }
    };
  }, [user, selectedTicket]);

  // Load messages when viewing a ticket
  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setView('detail');
    setError('');

    const { data, error: msgError } = await db.tickets.getMessages(ticket.id);
    if (!msgError && data) {
      setMessages(data);
      setTimeout(scrollToBottom, 100);
    }

    // Subscribe to new messages
    if (messageChannelRef.current) {
      realtime.unsubscribe(messageChannelRef.current);
    }
    const channel = realtime.subscribeToTicketMessages(ticket.id, (payload) => {
      if (payload.eventType === 'INSERT') {
        setMessages(prev => [...prev, payload.new]);
        setTimeout(scrollToBottom, 100);
      }
    });
    messageChannelRef.current = channel;
  };

  // Cleanup message subscription on unmount or view change
  useEffect(() => {
    return () => {
      if (messageChannelRef.current) {
        realtime.unsubscribe(messageChannelRef.current);
      }
    };
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    if (!subject.trim() || !message.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (openCount >= MAX_OPEN_TICKETS) {
      setError(`You can have a maximum of ${MAX_OPEN_TICKETS} open tickets`);
      return;
    }

    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session expired. Please log in again.');
        setCreating(false);
        return;
      }

      const resp = await supabase.functions.invoke('create-ticket', {
        body: {
          category,
          priority,
          subject: subject.trim(),
          message: message.trim(),
        },
      });

      if (resp.error) {
        setError(resp.error.message || 'Failed to create ticket');
        setCreating(false);
        return;
      }

      const result = resp.data;
      if (!result.success) {
        setError(result.error || 'Failed to create ticket');
        setCreating(false);
        return;
      }

      // Reset form
      setSubject('');
      setMessage('');
      setCategory('support');
      setPriority('medium');

      // Refresh and go to list
      await loadTickets();
      setView('list');
    } catch (err) {
      setError('Failed to create ticket. Please try again.');
    }
    setCreating(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket || sending) return;

    setError('');
    setSending(true);
    try {
      const resp = await supabase.functions.invoke('ticket-message', {
        body: {
          ticket_id: selectedTicket.id,
          message: replyMessage.trim(),
        },
      });

      if (resp.error) {
        setError(resp.error.message || 'Failed to send message');
        setSending(false);
        return;
      }

      const result = resp.data;
      if (!result.success) {
        setError(result.error || 'Failed to send message');
        setSending(false);
        return;
      }

      setReplyMessage('');
    } catch (err) {
      setError('Failed to send message. Please try again.');
    }
    setSending(false);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const categoryLabels = { support: 'Support', commercial: 'Commercial', resell: 'Resell', other: 'Other' };
  const priorityLabels = { low: 'Low', medium: 'Medium', high: 'High' };

  // LIST VIEW
  if (view === 'list') {
    return (
      <div className="tickets-page">
        <div className="tickets-header">
          <div>
            <h1>Support Tickets</h1>
            <p className="tickets-subtitle">
              {openCount} open ticket{openCount !== 1 ? 's' : ''} · {MAX_OPEN_TICKETS - openCount} remaining
            </p>
          </div>
          <button
            className="tickets-create-btn"
            onClick={() => { setError(''); setView('create'); }}
            disabled={openCount >= MAX_OPEN_TICKETS}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Ticket
          </button>
        </div>

        {loading ? (
          <div className="tickets-loading">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="tickets-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>No tickets yet</p>
            <span>Create a ticket to get help from our support team</span>
          </div>
        ) : (
          <div className="tickets-list">
            {tickets.map(ticket => (
              <div
                key={ticket.id}
                className={`ticket-card ${ticket.status}`}
                onClick={() => openTicketDetail(ticket)}
              >
                <div className="ticket-card-top">
                  <div className="ticket-card-left">
                    <span className={`ticket-status-badge ${ticket.status}`}>
                      {ticket.status === 'open' ? 'Open' : 'Closed'}
                    </span>
                    <span className={`ticket-priority-badge ${ticket.priority}`}>
                      {priorityLabels[ticket.priority]}
                    </span>
                    <span className="ticket-category-badge">
                      {categoryLabels[ticket.category]}
                    </span>
                  </div>
                  <span className="ticket-date">{formatDate(ticket.created_at)}</span>
                </div>
                <div className="ticket-card-subject">{ticket.subject}</div>
                <div className="ticket-card-id">#{ticket.id.slice(0, 8)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // CREATE VIEW
  if (view === 'create') {
    return (
      <div className="tickets-page">
        <div className="tickets-header">
          <div>
            <button className="tickets-back-btn" onClick={() => setView('list')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
              Back
            </button>
            <h1>Create Ticket</h1>
          </div>
        </div>

        {error && <div className="tickets-error">{error}</div>}

        <form className="ticket-create-form" onSubmit={handleCreate}>
          <div className="ticket-form-row">
            <div className="ticket-form-group">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label} — {c.desc}</option>
                ))}
              </select>
            </div>
            <div className="ticket-form-group">
              <label>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}>
                {PRIORITIES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ticket-form-group">
            <label>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
              maxLength={200}
              required
            />
          </div>

          <div className="ticket-form-group">
            <label>Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Describe your issue in detail..."
              maxLength={4000}
              rows={6}
              required
            />
          </div>

          <button type="submit" className="ticket-submit-btn" disabled={creating}>
            {creating ? 'Creating...' : 'Create Ticket'}
          </button>
        </form>
      </div>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && selectedTicket) {
    const isOpen = selectedTicket.status === 'open';

    return (
      <div className="tickets-page">
        <div className="tickets-header">
          <div>
            <button className="tickets-back-btn" onClick={() => {
              setView('list');
              setSelectedTicket(null);
              setMessages([]);
              if (messageChannelRef.current) {
                realtime.unsubscribe(messageChannelRef.current);
                messageChannelRef.current = null;
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
              Back
            </button>
            <h1>{selectedTicket.subject}</h1>
            <div className="ticket-detail-meta">
              <span className={`ticket-status-badge ${selectedTicket.status}`}>
                {selectedTicket.status === 'open' ? 'Open' : 'Closed'}
              </span>
              <span className={`ticket-priority-badge ${selectedTicket.priority}`}>
                {priorityLabels[selectedTicket.priority]}
              </span>
              <span className="ticket-category-badge">
                {categoryLabels[selectedTicket.category]}
              </span>
              <span className="ticket-detail-id">#{selectedTicket.id.slice(0, 8)}</span>
              <span className="ticket-detail-date">{formatDate(selectedTicket.created_at)}</span>
            </div>
          </div>
        </div>

        {error && <div className="tickets-error">{error}</div>}

        <div className="ticket-messages-container">
          <div className="ticket-messages">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`ticket-message ${msg.sender_type}`}
              >
                <div className="ticket-message-header">
                  <span className="ticket-message-sender">
                    {msg.sender_type === 'staff' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E8FF00" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    )}
                    {msg.sender_name}
                  </span>
                  <span className="ticket-message-time">
                    {formatDate(msg.created_at)} {formatTime(msg.created_at)}
                  </span>
                </div>
                <div className="ticket-message-body">{msg.message}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {isOpen ? (
            <form className="ticket-reply-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                value={replyMessage}
                onChange={e => setReplyMessage(e.target.value)}
                placeholder="Type your message..."
                maxLength={4000}
              />
              <button type="submit" disabled={sending || !replyMessage.trim()}>
                {sending ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22,2 15,22 11,13 2,9" />
                  </svg>
                )}
              </button>
            </form>
          ) : (
            <div className="ticket-closed-banner">
              This ticket has been closed. You can view the conversation history but cannot send new messages.
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default TicketsPage;
